import { teamRoundHoles } from "./util"
import { Round, TeamRound, TeamRoundPlayer, HoleScore, TeamRoundPlayerScoreMap, HoleSkin, Hole } from "./types"

export default function calculateScores(round: Round, scores: TeamRoundPlayerScoreMap) {
  // We need to clone the teamRounds since we are going to modify them
  const teamRounds = JSON.parse(JSON.stringify(round.teamRounds))

  const playerIndex = new Map<number, { player: TeamRoundPlayer; teamRound: TeamRound }>()

  for (const teamRound of teamRounds) {
    teamRound.grossScore = 0
    teamRound.grossScoreRelativeToPar = 0
    teamRound.netScore = 0
    teamRound.netScoreRelativeToPar = 0
    teamRound.holesCompleted = 0
    for (const player of teamRound.players) {
      player.grossScore = 0
      player.grossScoreRelativeToPar = 0
      player.netScore = 0
      player.netScoreRelativeToPar = 0
      player.holesCompleted = 0
      playerIndex.set(player.id, {
        player: player,
        teamRound: teamRound
      })
    }
  }

  const skins = new Map<Hole, HoleSkin>()

  for (const [playerId, holeScores] of Object.entries(scores)) {
    const entry = playerIndex.get(parseInt(playerId))

    if (!entry) continue

    const { player, teamRound } = entry

    if (player.canceled) continue

    const holes = teamRoundHoles(round.holes, round.numberOfHoles, teamRound.startHoleNumber)

    for (const [i, grossScore] of holeScores.entries()) {
      if (!grossScore) continue

      const hole = holes[i]
      if (!hole) {
        reportMissingHole(round, teamRound, i)
        continue
      }

      if (!player.scores) {
        player.scores = new Map<number, HoleScore>()
      }

      const handicap = playerHoleHandicap(hole.handicap, player.handicapStrokes)
      const netScore = grossScore ? grossScore - handicap : null

      const playerHoleScore: HoleScore = {
        grossScore: grossScore,
        grossScoreRelativeToPar: grossScore || grossScore === 0 ? grossScore - hole.par : null,
        netScore: netScore,
        netScoreRelativeToPar: netScore || netScore === 0 ? netScore - hole.par : null
      }

      player.scores.set(hole.id, playerHoleScore)

      sumHoleScores(player)

      if (!teamRound.scores) {
        teamRound.scores = new Map<number, HoleScore>()
      }

      let teamHoleScore = teamRound.scores.get(hole.id)
      if (teamHoleScore) {
        if (playerHoleScore.netScore < teamHoleScore.netScore) {
          teamRound.scores.set(hole.id, { ...playerHoleScore })
          for (let player of teamRound.players) {
            let playerHoleScore = player.scores?.get(hole.id)
            if (playerHoleScore) {
              playerHoleScore.usedForTeamScore = false
            }
          }
          playerHoleScore.usedForTeamScore = true
        } else if (playerHoleScore.netScore === teamHoleScore.netScore) {
          for (let player of teamRound.players) {
            let playerHoleScore = player.scores?.get(hole.id)
            if (playerHoleScore) {
              playerHoleScore.usedForTeamScore = false
            }
          }
        } else {
          playerHoleScore.usedForTeamScore = false
        }
      } else {
        teamRound.scores.set(hole.id, { ...playerHoleScore })
        playerHoleScore.usedForTeamScore = true
      }

      sumHoleScores(teamRound)

      const skin = skins.get(hole)
      if (skin == null || playerHoleScore.netScoreRelativeToPar < skin.score) {
        skins.set(hole, {
          hole: hole,
          users: [player.user],
          score: playerHoleScore.netScoreRelativeToPar
        })
      } else if (playerHoleScore.netScoreRelativeToPar == skin.score) {
        skin.users.push(player.user)
      }
    }
  }

  // Sort By:
  // - Teams that have started, then team that have not
  // - Lowest net score
  // - Most holes completed
  // - Earliest Tee Time
  // - Lowest Starting Hole
  // - Lowest Starting Position (A then B)
  // - Team who registered first
  teamRounds.sort((a, b) => {
    return (
      (b.complete && (!b.holesCompleted || b.holesCompleted < 9) ? 0 : 1) -
        (a.complete && (!a.holesCompleted || a.holesCompleted < 9) ? 0 : 1) ||
      (a.holesCompleted ? 0 : 1) - (b.holesCompleted ? 0 : 1) ||
      (a.netScoreRelativeToPar || 0) - (b.netScoreRelativeToPar || 0) ||
      (b.holesCompleted || 0) - (a.holesCompleted || 0) ||
      (a.startTimeOfDay || "0").localeCompare(b.startTimeOfDay || "0") ||
      (a.startHoleNumber || 0) - (b.startHoleNumber || 0) ||
      (a.startGroup || 0) - (b.startGroup || 0) ||
      (a.id < b.id ? -1 : 1)
    )
  })

  calculatePositions(
    teamRounds,
    round.points.team.map((p) => p.points)
  )

  const individualPlayers = Array.from(playerIndex.values()).map((o) => o.player)

  individualPlayers.sort((a, b) => {
    return (
      (b.complete && (!b.holesCompleted || b.holesCompleted < 9) ? 0 : 1) -
        (a.complete && (!a.holesCompleted || a.holesCompleted < 9) ? 0 : 1) ||
      (a.holesCompleted ? 0 : 1) - (b.holesCompleted ? 0 : 1) ||
      (a.netScoreRelativeToPar || 0) - (b.netScoreRelativeToPar || 0) ||
      (b.holesCompleted || 0) - (a.holesCompleted || 0) ||
      (a.id < b.id ? -1 : 1)
    )
  })

  calculatePositions(
    individualPlayers,
    round.points.individual.map((p) => p.points)
  )

  let skinsWon = 0
  for (const skin of Array.from(skins.values())) {
    if (skin.users.length == 1) {
      skinsWon += 1
    }
  }

  const skinValue = roundDecimal(round.skinsPot / skinsWon)
  for (const skin of Array.from(skins.values())) {
    if (skin.users.length == 1) {
      skin.amount = skinValue
    } else {
      skin.amount = null
    }
  }

  return {
    teamRounds: teamRounds,
    skins: skins
  }
}

// calculateScores runs on every render, so only report a team round with a score we can't place on a hole once.
const reportedMissingHoles = new Set<number>()

function reportMissingHole(round: Round, teamRound: TeamRound, scoreIndex: number) {
  if (reportedMissingHoles.has(teamRound.id)) return
  reportedMissingHoles.add(teamRound.id)

  const context = {
    teamRoundId: teamRound.id,
    scoreIndex,
    startHoleNumber: teamRound.startHoleNumber,
    numberOfHoles: round.numberOfHoles,
    holesLength: round.holes.length
  }
  console.warn("calculateScores skipped a score because hole is missing", context)
  if (typeof window !== "undefined" && window.notifyHoneybadger) {
    window.notifyHoneybadger("calculateScores skipped a score because hole is missing", context)
  }
}

function playerHoleHandicap(holeHandicap, playerHandicap) {
  let remainingHandicap = playerHandicap
  let strokes = 0

  if (remainingHandicap >= holeHandicap) {
    strokes += 1
  }

  remainingHandicap -= 9

  if (remainingHandicap >= holeHandicap) {
    strokes += 1
  }

  return strokes
}

function sumHoleScores(object) {
  object.grossScore = 0
  object.grossScoreRelativeToPar = 0
  object.netScore = 0
  object.netScoreRelativeToPar = 0
  object.holesCompleted = 0
  for (let holeScore of object.scores.values()) {
    object.grossScore += holeScore.grossScore
    object.grossScoreRelativeToPar += holeScore.grossScoreRelativeToPar
    object.netScore += holeScore.netScore
    object.netScoreRelativeToPar += holeScore.netScoreRelativeToPar
    object.holesCompleted += 1
  }
}

function calculatePositions(objects, totalPoints) {
  const groupedObjects = objects.reduce((groups, o) => {
    const group = groups.length > 0 ? groups[groups.length - 1] : []
    const last = group.length > 0 ? group[group.length - 1] : []
    const lastTeamStarted = !!last.holesCompleted
    const teamStarted = !!o.holesCompleted
    if (
      last &&
      last.netScoreRelativeToPar === o.netScoreRelativeToPar &&
      lastTeamStarted === teamStarted &&
      !(o.complete && (!o.holesCompleted || o.holesCompleted < 9))
    ) {
      group.push(o)
    } else {
      groups.push([o])
    }
    return groups
  }, [])

  let rank = 1
  for (const g of groupedObjects) {
    const groupPoints = totalPoints.splice(0, g.length).reduce((sum, points) => sum + points)

    const points = Math.round(groupPoints / g.length)

    for (const o of g) {
      const tied = g.length > 1
      o.position = {
        rank: rank,
        tied: tied,
        label: tied ? `T${rank}` : `${rank}`,
        points: o.complete && (!o.holesCompleted || o.holesCompleted < 9) ? 0 : points
      }
    }
    rank += g.length
  }
}

function roundDecimal(num) {
  const m = Number((Math.abs(num) * 100).toPrecision(15))
  return (Math.round(m) / 100) * Math.sign(num)
}
